/**
 * Owns the entire notes concern for the ExceptionDrawer - the merged
 * current + sibling feed, the composer (draft / open / posting / focus /
 * flash) and the create/edit/delete handlers. Keeping it in one hook lets
 * ``ExceptionDrawer`` stay focused on layout. The fetch is lazy: gated on
 * ``active`` so the Notes tab pays for the feed only when first opened.
 *
 * History (``useExceptionObjectHistory``) is intentionally NOT owned here -
 * it feeds the Activity tab + the object action line's age, so the drawer
 * fetches it once (eagerly) and passes it down.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useExceptionNotes,
  type NoteRowKey,
} from '../../../../hooks/useExceptionNotes'
import { useSiblingNotes } from '../../../../hooks/useSiblingNotes'
import {
  findDisallowedLinks,
  disallowedLinksMessage,
} from '../../../../utils/linkifyNoteBody'
import type { NoteFeedItem } from './types'

export const BODY_MAX = 2000
const BODY_WARN_AT = 1900

interface UseDrawerNotesArgs {
  /** Canonical note key for the current row's exception thread, or null when
   *  there is no current exception (object-only drawer) / not gated. */
  key: NoteRowKey | null
  /** Object identity (fp, data_domain, object_id) for the object-level note
   *  fetch. Present even when ``key`` is null so a zero-issue row still loads
   *  every comment ever left on the object. */
  objectKey: {
    fp_code: string
    data_domain: string
    object_id: string
  } | null
  notesGated: boolean
  /** Drawer open flag - drives the per-row reset. */
  open: boolean
  /**
   * Whether the Notes feed should fetch yet. The drawer flips this true on the
   * first Notes-tab open so the (current + sibling) note requests are lazy -
   * Issues/Activity tabs never pay for them. Stays true for the session once
   * opened. Defaults handled by the caller.
   */
  active: boolean
  /** Short exception-type name for labelling the current thread's notes. */
  exceptionTypeName?: string
  onCountDelta?: (delta: number) => void
}

export interface DrawerNotes {
  // Feed
  loading: boolean
  error: string | null
  siblingLoading: boolean
  siblingError: string | null
  noteFeed: NoteFeedItem[]
  noteCount: number
  // Composer
  draft: string
  setDraft: (v: string) => void
  setComposerOpen: (v: boolean) => void
  posting: boolean
  composerFlash: boolean
  composerInputRef: React.RefObject<HTMLTextAreaElement>
  charCount: number
  overLimit: boolean
  warnCount: boolean
  linkError: string | null
  canPost: boolean
  // Actions
  handleAddNote: () => void
  handlePost: () => Promise<void>
  handleEdit: (id: number, body: string) => Promise<void>
  handleDelete: (id: number) => Promise<void>
}

export const useDrawerNotes = ({
  key,
  objectKey,
  notesGated,
  open,
  active,
  exceptionTypeName,
  onCountDelta,
}: UseDrawerNotesArgs): DrawerNotes => {
  // Lazy: no fetch until the Notes tab has been opened (active). create/update/
  // remove still bind to the live key so posting works the moment it activates.
  // ``key`` is null for an object-only drawer (no current exception) - the
  // current-thread fetch then stays idle and every note arrives via siblings.
  const { notes, loading, error, create, update, remove } = useExceptionNotes(
    notesGated && active ? key : null,
  )

  // Object-level note fetch keyed off the object identity, not the current
  // thread - so it loads even when there is no current exception. When a
  // current thread exists it is excluded (the merged feed adds it back via
  // ``notes`` above); object-only drawers omit the exclusion and get every
  // note on the object.
  const siblingKey =
    notesGated && active && objectKey
      ? {
          fp_code: objectKey.fp_code,
          data_domain: objectKey.data_domain,
          object_id: objectKey.object_id,
          exclude_exception_code: key?.exception_code,
        }
      : null
  const {
    notes: siblingNotes,
    loading: siblingLoading,
    error: siblingError,
  } = useSiblingNotes(siblingKey)

  const [draft, setDraft] = useState('')
  // Drives the focus effect (focus the textarea when the user jumps to it via
  // "Add note"); the composer textarea itself is always rendered in the footer.
  const [composerOpen, setComposerOpen] = useState(false)
  const [posting, setPosting] = useState(false)
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null)
  // Transient highlight on the composer after "Add note" so the cursor jump to
  // the input is obvious (focus alone is easy to miss).
  const [composerFlash, setComposerFlash] = useState(false)

  const focusComposer = useCallback(() => {
    setComposerOpen(true)
    setComposerFlash(true)
    window.setTimeout(() => setComposerFlash(false), 1400)
  }, [])

  // Focus the textarea when "Add note" opens the composer (preventScroll - it's
  // the footer, already in view).
  useEffect(() => {
    if (!composerOpen) return
    composerInputRef.current?.focus({ preventScroll: true })
  }, [composerOpen])

  // "Add note" from another tab's footer: the drawer switches to the Notes tab;
  // this focuses + flashes the composer.
  const handleAddNote = useCallback(() => {
    focusComposer()
  }, [focusComposer])

  // Reset the composer on a new row / re-open.
  useEffect(() => {
    if (open) {
      setDraft('')
      setComposerOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key?.exception_code, key?.object_id])

  const charCount = draft.length
  const overLimit = charCount > BODY_MAX
  const warnCount = charCount >= BODY_WARN_AT && !overLimit
  // Block posting a note that embeds a link to a non-allowlisted domain. The
  // backend rejects it too (validate_note_links); this is the inline guard.
  const disallowedLinks = useMemo(() => findDisallowedLinks(draft), [draft])
  const linkError = disallowedLinksMessage(disallowedLinks)
  const canPost = key !== null

  const handlePost = useCallback(async () => {
    const trimmed = draft.trim()
    if (!trimmed || trimmed.length > BODY_MAX || !key || linkError) return
    setPosting(true)
    try {
      await create(trimmed)
      setDraft('')
      setComposerOpen(false)
      onCountDelta?.(1)
    } finally {
      setPosting(false)
    }
  }, [draft, key, linkError, create, onCountDelta])

  const handleEdit = useCallback(
    async (id: number, body: string) => {
      await update(id, body)
    },
    [update],
  )

  const handleDelete = useCallback(
    async (id: number) => {
      await remove(id)
      onCountDelta?.(-1)
    },
    [remove, onCountDelta],
  )

  // Merged feed: current-thread + sibling notes in one chronological list
  // (newest first). A single NoteItem renders both; the card decides whether
  // to expose edit/delete (current) or a deep-link tag (sibling).
  const noteFeed = useMemo<NoteFeedItem[]>(() => {
    const currentDomain = key?.data_domain ?? ''
    const currentCode = key?.exception_code ?? ''
    const currentName = exceptionTypeName || currentCode
    const fromCurrent: NoteFeedItem[] = notes.map((c) => ({
      key: `current-${c.id}`,
      source: 'current',
      note: c,
      exceptionCode: currentCode,
      exceptionName: currentName,
      dataDomain: currentDomain,
    }))
    const fromSiblings: NoteFeedItem[] = siblingNotes.map((c) => ({
      key: `sibling-${c.id}`,
      source: 'sibling',
      note: c,
      exceptionCode: c.exception_code,
      exceptionName: c.exception_name || c.exception_code,
      dataDomain: c.data_domain,
    }))
    return [...fromCurrent, ...fromSiblings].sort((a, b) =>
      b.note.created_at.localeCompare(a.note.created_at),
    )
  }, [notes, siblingNotes, key?.data_domain, key?.exception_code, exceptionTypeName])

  return {
    loading,
    error,
    siblingLoading,
    siblingError,
    noteFeed,
    noteCount: notes.length + siblingNotes.length,
    draft,
    setDraft,
    setComposerOpen,
    posting,
    composerFlash,
    composerInputRef,
    charCount,
    overLimit,
    warnCount,
    linkError,
    canPost,
    handleAddNote,
    handlePost,
    handleEdit,
    handleDelete,
  }
}
